import React, { useEffect, useState, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import axios from "axios";
import PlanTable from "../plans/PlanTable";
import MobilitySummary from "../results/MobilitySummary";
import DataLoader from "../results/DataLoader";
import MobilityMap from "../results/map";

const API_BASE = "https://opt-api.vercel.app";

export default function CampaignDetail() {
    const { id } = useParams();
    const history = useHistory();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [plan, setPlan] = useState(null);
    const [datasets, setDatasets] = useState([]);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                setLoading(true);
                setError("");
                const { data } = await axios.get(`${API_BASE}/Plans/${id}`);
                if (!alive) return;
                setPlan(data);
            } catch (e) {
                if (!alive) return;
                setError(e?.response?.data?.hata || e.message || "Plan alınamadı");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [id]);

    // 1) sadece mobility olan dataset'ler
    const mobilityDatasets = useMemo(
        () => (datasets || []).filter(d => d.type === "mobility"),
        [datasets]
    );

    // 2) yardımcılar: alan okuma + normalize + benzerlik
    const getVal = (obj, keys) => {
        const lowerMap = Object.fromEntries(Object.entries(obj || {}).map(([k, v]) => [k.toLowerCase(), v]));
        for (const k of keys) if (lowerMap.hasOwnProperty(k)) return lowerMap[k];
        return undefined;
    };
    const coerceNum = (v) => {
        const n = typeof v === "string" ? Number(v.replace(",", ".")) : Number(v);
        return Number.isFinite(n) ? n : undefined;
    };
    const normalize = (s) =>
        String(s || "")
            .toLowerCase()
            .replaceAll("ç", "c").replaceAll("ğ", "g").replaceAll("ı", "i").replaceAll("ş", "s").replaceAll("ö", "o").replaceAll("ü", "u")
            .replace(/[^\p{L}\p{N}\s]/gu, " ") // noktalama vs
            .replace(/\s+/g, " ")
            .trim();

    const jaccard = (a, b) => {
        const A = new Set(normalize(a).split(" ").filter(Boolean));
        const B = new Set(normalize(b).split(" ").filter(Boolean));
        if (!A.size || !B.size) return 0;
        let inter = 0;
        A.forEach(t => { if (B.has(t)) inter++; });
        const uni = A.size + B.size - inter;
        return inter / uni;
    };

    const similarity = (a, b) => {
        const na = normalize(a), nb = normalize(b);
        if (!na || !nb) return 0;
        if (na.includes(nb) || nb.includes(na)) return 1;     // güçlü “içerme”
        return jaccard(na, nb);
    };

    // 3) mobility datasından potansiyel noktaları çıkar
    const mobilityPoints = useMemo(() => {
        const acc = [];
        for (const ds of mobilityDatasets) {
            const arr = Array.isArray(ds) ? ds : (Array.isArray(ds.rows) ? ds.rows : (Array.isArray(ds.data) ? ds.data : (Array.isArray(ds.records) ? ds.records : [])));
            arr.forEach((item, idx) => {
                if (!item || typeof item !== "object") return;
                const lat = coerceNum(getVal(item, ["lat", "latitude", "enlem"]));
                const lng = coerceNum(getVal(item, ["lng", "lon", "long", "longitude", "boylam"]));
                if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

                const addressLike =
                    getVal(item, ["address", "adres", "location", "konum", "title", "name", "açıklama"]) || "";

                const img = getVal(item, ["img", "image", "photo", "thumbnail"]);
                const id = item.id ?? `mob-${acc.length}-${idx}`;
                acc.push({ id, lat, lng, addressLike, img, raw: item });
            });
        }
        return acc;
    }, [mobilityDatasets]);

    // 4) plan.addresses → en iyi mobility eşleşmesi
    const matchedPoints = useMemo(() => {
        if (!plan?.addresses?.length || !mobilityPoints.length) return [];
        const used = new Set();
        const out = [];

        plan.addresses.forEach((addrObj, i) => {
            const addrText = addrObj?.address || "";
            let best = null;
            let bestScore = -1;

            mobilityPoints.forEach((p) => {
                if (used.has(p.id)) return; // aynı mobility noktasını birden fazla adrese bağlama
                const s = similarity(addrText, p.addressLike || addrText);
                if (s > bestScore) {
                    best = p;
                    bestScore = s;
                }
            });

            // eşik: 0.45 (gerekirse düşür/ yükselt)
            if (best && bestScore >= 0.45) {
                used.add(best.id);
                out.push({
                    id: addrObj._id || `plan-${i}`,
                    lat: best.lat,
                    lng: best.lng,
                    title: addrObj.address,     // InfoWindow başlığı: planın orijinal adresi
                    img: best.img,              // varsa mobility'den görsel
                    _score: bestScore,
                    _matchedFrom: best.addressLike,
                });
            }
        });

        return out;
    }, [plan, mobilityPoints]);

    return (
        <div className="max-w-5xl mx-auto px-4 py-8">
            <button onClick={() => history.push("/homepage")} className="mb-4 h-9 px-3 border rounded-lg text-sm">
                ← Geri Dön
            </button>

            <div className="flex justify-end mb-3">
                <DataLoader onLoaded={(files) => setDatasets(prev => [...prev, ...files])} />
            </div>

            {loading && <div className="text-slate-600">Yükleniyor…</div>}
            {!loading && error && <div className="text-rose-600">{error}</div>}

            {!loading && !error && plan && (
                <>
                    <div className="space-y-1 border rounded-lg p-4 mb-4">
                        <h2 className="text-xl font-semibold">{plan.name}</h2>
                        {plan.notes ? <div className="text-sm text-slate-600"><b>Notes:</b> {plan.notes}</div> : null}
                    </div>

                    <PlanTable
                        rows={plan.addresses || []}
                        totalBudget={plan.totalBudget}
                        currency={plan.currency || "TRY"}
                    />
                    <MobilityMap points={matchedPoints} />


                    <MobilitySummary plan={plan} datasets={mobilityDatasets} />

                </>
            )}
        </div>
    );
}
